import { decodeBasicHtmlEntities } from './htmlEntities'
import { hasSyncedTweetData } from './twitterUrlHydration'

const TWEET_COLLAPSED_TEXT_LIMIT = 280

// X escapes only &, < and > in its API text; the regex decoder covers those without touching the
// DOM, which matters because the height estimators run this outside a render.
export const decodeTweetText = value =>
    decodeBasicHtmlEntities(String(value)).replace(/\u00a0/g, ' ')

export const tweetStatusRegexp =
    /^https?:\/\/(?:www\.|mobile\.)?(?:x|twitter)\.com\/[^/]+\/status(?:es)?\/(\d+)/i

export const tweetStatusId = address =>
    (typeof address === 'string' && address.match(tweetStatusRegexp)?.[1]) || null

// The post a url record points at: its explicit id fields, then its address. A numeric
// service_id is skipped on purpose — post ids exceed 2^53 and arrive with their last digits
// rounded when serialized as a JSON number.
export const getTweetId = url => {
    const explicitId = url?.tweet_id || url?.service_json?.id
    if (explicitId) return String(explicitId)
    if (typeof url?.service_id === 'string') return url.service_id
    return tweetStatusId(url?.address)
}

// Some historical X payloads carry blank entries in image_urls/video_urls; only a real source
// renders (MediaGrid has nothing to draw into otherwise).
export const renderableTweetMediaUrl = value => typeof value === 'string' && value.trim().length > 0

// A card whose media is still syncing already shows the reserved media box in the link's place.
const rendersTweetMedia = url =>
    url?.tweet_media_pending === true ||
    [
        ...(Array.isArray(url?.video_urls) ? url.video_urls : []),
        ...(Array.isArray(url?.image_urls) ? url.image_urls : []),
    ].some(renderableTweetMediaUrl)

// The quoted post this card shows as a synced nested quote card (url.embed). Its t.co link is
// dropped from the text only then: while the quote is unsynced, or once the quoted post is gone
// (the card then says "unavailable" and links nowhere), the reader keeps a way to reach it.
const renderedQuoteTweetId = url => {
    const embed = url?.embed
    if (!embed || embed.service_name !== 'twitter' || !hasSyncedTweetData(embed)) return null
    return getTweetId(embed)
}

// The API text this card displays plus the entity list that indexes it. X abbreviates a long
// post in `text` and carries the full body in `note_tweet` with its own entities. `selection`
// is the backend's mirror of that text, but Url#selection serves it HTML-decoded while the
// offsets index the escaped API text, so it only stands in when the API text is absent.
//
// `collapsible` is X's own rule for the "more" toggle: only a long post (one with a
// `note_tweet` body) folds. A regular post already fits X's 280 weighted characters — its
// display text can only run past 280 through a display_url longer than the 23-char t.co it
// replaces, and a "more" that reveals two words is noise.
const tweetTextSource = url => {
    const serviceJson = url?.service_json || {}
    const noteText = serviceJson.note_tweet?.text
    if (typeof noteText === 'string' && noteText) {
        return { text: noteText, entities: serviceJson.note_tweet.entities, collapsible: true }
    }
    if (typeof serviceJson.text === 'string' && serviceJson.text) {
        return { text: serviceJson.text, entities: serviceJson.entities, collapsible: false }
    }
    if (url?.selection) return { text: String(url.selection), entities: null, collapsible: true }
    return null
}

// X API v2 offsets are Unicode code-point indices (an emoji counts once) over the raw text,
// i.e. before its `&amp;`/`&lt;`/`&gt;` escapes are decoded.
const entityRange = entity => {
    const { start, end } = entity || {}
    if (!Number.isInteger(start) || !Number.isInteger(end)) return null
    if (start < 0 || end <= start) return null
    return [start, end]
}

const entitySpans = (url, entities, codePoints) => {
    const spans = []
    const add = (list, build) => {
        if (!Array.isArray(list)) return
        list.forEach(entity => {
            const range = entityRange(entity)
            if (!range || range[1] > codePoints.length) return

            const raw = codePoints.slice(range[0], range[1]).join('')
            const span = build(entity, raw)
            if (span) spans.push({ start: range[0], end: range[1], ...span })
        })
    }

    // X appends one t.co per attached photo/video; the card renders the media in its place —
    // when it has the media. A quoted post's media is never synced, so its card keeps the link.
    const mediaRenders = rendersTweetMedia(url)
    add(entities?.urls, (entity, raw) => ({
        type: 'link',
        text: entity.display_url || raw,
        href: entity.expanded_url || entity.url || raw,
        hidden: !!entity.media_key && mediaRenders,
    }))
    add(entities?.mentions, (entity, raw) =>
        entity.username
            ? { type: 'mention', text: raw, href: `https://x.com/${entity.username}` }
            : null,
    )
    add(entities?.hashtags, (entity, raw) =>
        entity.tag
            ? {
                  type: 'hashtag',
                  text: raw,
                  href: `https://x.com/hashtag/${encodeURIComponent(entity.tag)}`,
              }
            : null,
    )
    add(entities?.cashtags, (entity, raw) =>
        entity.tag
            ? {
                  type: 'cashtag',
                  text: raw,
                  href: `https://x.com/search?q=%24${encodeURIComponent(entity.tag)}`,
              }
            : null,
    )

    // A four-photo post repeats one t.co range per photo; overlapping spans keep the first
    // (longest, and a media link over a plain one at the same range).
    spans.sort(
        (a, b) => a.start - b.start || b.end - a.end || Number(!!b.hidden) - Number(!!a.hidden),
    )
    let cursor = 0
    return spans.filter(span => {
        if (span.start < cursor) return false
        cursor = span.end
        return true
    })
}

const textSegment = text => ({ type: 'text', text })

const trimSegmentsEnd = segments => {
    while (segments.length) {
        const last = segments[segments.length - 1]
        if (last.type !== 'text') return segments

        const trimmed = last.text.replace(/\s+$/, '')
        if (trimmed) {
            segments[segments.length - 1] = textSegment(trimmed)
            return segments
        }
        segments.pop()
    }
    return segments
}

const segmentsText = segments => segments.map(segment => segment.text).join('')

// The post text as X renders it: plain runs (entities decoded, line breaks kept) interleaved
// with link/mention/hashtag/cashtag segments, minus the t.co links the card shows as media or
// as the quote card.
const buildTweetSegments = (url, { text, entities }) => {
    const codePoints = Array.from(text)
    const spans = entitySpans(url, entities, codePoints)

    const segments = []
    let cursor = 0
    let afterHiddenLink = false
    const pushText = raw => {
        let text = decodeTweetText(raw)
        const last = segments[segments.length - 1]
        // A dropped link leaves its separators on both sides; keep one.
        if (afterHiddenLink && /\s$/.test(last?.text || '')) text = text.replace(/^[ \t]+/, '')
        afterHiddenLink = false
        if (!text) return
        if (last?.type === 'text') last.text += text
        else segments.push(textSegment(text))
    }
    spans.forEach(span => {
        pushText(codePoints.slice(cursor, span.start).join(''))
        if (span.hidden) afterHiddenLink = true
        else segments.push({ type: span.type, text: span.text, href: span.href })
        cursor = span.end
    })
    pushText(codePoints.slice(cursor).join(''))
    trimSegmentsEnd(segments)

    // X drops the link to the quoted post from the text when it renders the quote card. It is
    // the last thing in the text, so only a trailing match goes.
    const quoteTweetId = renderedQuoteTweetId(url)
    const last = segments[segments.length - 1]
    if (quoteTweetId && last?.type === 'link' && tweetStatusId(last.href) === quoteTweetId) {
        segments.pop()
        trimSegmentsEnd(segments)
    }

    return segments
}

// Word-preserving cut at `limit` characters, mirroring textUtils.truncateText but over segments:
// line breaks survive (the card renders them) and a link is never split — the cut lands before
// it instead.
const truncateSegments = (segments, limit) => {
    const budget = limit - 3
    const kept = []
    let length = 0

    for (const segment of segments) {
        if (length + segment.text.length <= budget) {
            kept.push(segment)
            length += segment.text.length
            continue
        }
        if (segment.type === 'text') {
            const room = budget - length
            const head = segment.text.slice(0, room)
            // The head is whole when the next character is whitespace; otherwise drop the word
            // the cut landed in — unless that unbroken run is all there is, in which case
            // hard-cut it without leaving half of a surrogate pair behind.
            const cut = /\s/.test(segment.text.charAt(room)) ? head : head.replace(/\S*$/, '')
            if (cut) kept.push(textSegment(cut))
            else if (!length) kept.push(textSegment(head.replace(/[\uD800-\uDBFF]$/, '')))
        }
        break
    }

    const trimmed = trimSegmentsEnd(kept)
    const last = trimmed[trimmed.length - 1]
    if (last?.type === 'text') {
        // The ellipsis takes the place of any period, comma or colon the cut landed after, so
        // "noticed this." reads "noticed this..." rather than "noticed this....".
        trimmed[trimmed.length - 1] = textSegment(`${last.text.replace(/[\s.,;:…]+$/, '')}...`)
    } else {
        trimmed.push(textSegment('...'))
    }
    return trimmed
}

const buildTweetDisplay = url => {
    const source = tweetTextSource(url)
    if (!source) return null

    const segments = buildTweetSegments(url, source)
    const text = segmentsText(segments)
    const isTruncated = source.collapsible && text.length > TWEET_COLLAPSED_TEXT_LIMIT
    const collapsed = isTruncated
        ? truncateSegments(segments, TWEET_COLLAPSED_TEXT_LIMIT)
        : segments
    return {
        expanded: { segments, text },
        collapsed: { segments: collapsed, text: segmentsText(collapsed) },
        isTruncated,
    }
}

// The card, the row estimator and the quote window all ask for the same post's text several
// times per geometry pass; url objects are replaced (not mutated) when a post syncs, so the
// build is memoized per object, guarded by the fields it reads in case one is swapped in place.
const displayMemo = new WeakMap()
const displayInputs = url => [
    url.service_json,
    url.selection,
    url.embed,
    url.image_urls,
    url.video_urls,
]

const tweetDisplay = url => {
    if (!url || typeof url !== 'object') return null

    const inputs = displayInputs(url)
    const memoized = displayMemo.get(url)
    if (memoized && memoized.inputs.every((value, index) => value === inputs[index])) {
        return memoized.display
    }

    const display = buildTweetDisplay(url)
    displayMemo.set(url, { inputs, display })
    return display
}

export const getTweetDisplaySegments = (url, isExpanded = false) => {
    const display = tweetDisplay(url)

    if (display) {
        const shown = isExpanded ? display.expanded : display.collapsed
        return { segments: shown.segments, text: shown.text, isTruncated: display.isTruncated }
    }

    const fallback =
        url?.service_status === 'twitter_missing'
            ? 'This post is unavailable. Open it on X.'
            : 'Loading post…'
    return { segments: [textSegment(fallback)], text: fallback, isTruncated: false }
}

export const getTweetDisplayText = (url, isExpanded = false) => {
    const { text, isTruncated } = getTweetDisplaySegments(url, isExpanded)
    return { text, isTruncated }
}
